import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { DiscountCouponService } from './discount-coupon.service';
import { OrderFlowTraceService } from './telemetry/order-flow-trace.service';

/**
 * Landing do link compartilhável do Cupom v2: `https://<loja>.../c/<código>`, gerado na
 * tela nova de cupons do painel (buildShareLink).
 *
 * Não existe tela própria: o guard guarda o código como PENDENTE no
 * DiscountCouponService (o MESMO canal do pop-up de destaque e dos cupons públicos) e
 * devolve o consumidor ao cardápio da loja. O cardápio anuncia a chegada (toast no
 * menu-list) e a aplicação de verdade acontece na tela de pagamento
 * (applyPendingDiscountCoupon), onde o /validate enxerga o contexto completo do pedido.
 * Com a flag new_coupon OFF a degradação é silenciosa: o pendente nunca é consumido e o
 * cliente só cai no cardápio, como o catch-all já fazia antes desta rota existir.
 */
@Injectable({ providedIn: 'root' })
export class CouponLinkGuard implements CanActivate {
	constructor(private router: Router, private injector: Injector) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree {
		const slug = this.slugDaLoja(route);
		const code = CouponLinkGuard.normalize(route.params && route.params.couponCode);

		if (code) {
			try {
				this.injector.get(DiscountCouponService).setPendingCodeFromLink(code);
			} catch (e) { /* pendência nunca impede o consumidor de chegar ao cardápio */ }
		}

		// Fire-and-forget, como toda a telemetria: mede o uso do link (código sai hasheado
		// pela allowlist do step) sem poder atrapalhar a navegação. A const nomeada (em vez
		// de encadear o .emit no injector.get) é o que o check:telemetry-coverage resolve
		// como receptor do funil.
		try {
			const orderFlowTrace = this.injector.get(OrderFlowTraceService);
			orderFlowTrace.emit('ClientCouponLinkOpened', {
				out: { code: code, valid_format: !!code },
			}, 'CouponLinkGuard.canActivate');
		} catch (e) { /* telemetria nunca derruba navegação */ }

		return this.router.parseUrl(slug ? `/${slug}` : '/');
	}

	/**
	 * Espaço canônico do Cupom v2 (espelho do Repository::normalizeCode do server):
	 * translitera acento via NFD (Ç -> C), MAIÚSCULA, só A-Z0-9, corta em 35. O server
	 * normaliza a busca do /validate do mesmo jeito, então /c/promo-10-açaí acha o
	 * mesmo cupom que /c/PROMO10ACAI.
	 */
	private static normalize(raw: any): string {
		return String(raw || '')
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toUpperCase()
			.replace(/[^A-Z0-9]/g, '')
			.slice(0, 35);
	}

	private slugDaLoja(route: ActivatedRouteSnapshot): string {
		try {
			// A rota é filha de `:restaurant`, então o slug está no pai. A subida pela
			// cadeia cobre o caso de o Angular inserir um nível intermediário (mesmo
			// padrão do RouteNotFoundGuard).
			let atual: ActivatedRouteSnapshot = route;
			while (atual) {
				const slug = atual.params && atual.params.restaurant;
				if (slug) return slug;
				atual = atual.parent;
			}
		} catch (e) { /* cai no fallback */ }
		return '';
	}
}
